import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Skeleton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { ArrowBack, Equalizer, Forum, Info, ReceiptLong, WaterDamage } from '@mui/icons-material';
import { httpCodeNotFound } from '../../../utils/constants.utils';
import ContractDetailsUi from '../../../components/user-interfaces/contract/details/contract-details.ui';
import { Link, useNavigate, useParams } from 'react-router-dom';
import EcoGesture from '../../../components/others/eco-gesture/eco-gesture';
import BillsTable from '../../../components/table/bills/bills.table';
import { ContractDto } from '../../../services/dtos/contract.dto';
import { useConfirm } from 'material-ui-confirm';
import axios, { AxiosError } from 'axios';
import { detachedContract, getContract } from '../../../services/contracts.service';
import { getBillBase64, getLastBill } from '../../../services/bills.service';
import { BillDto } from '../../../services/dtos/bill.dto';
import { showAxiosMessageError } from '../../../services/error-message.service';
import ContractRequests from '../../../components/others/contract-request/contract-requests';
import { stopDirectDebit } from '../../../services/requests.service';
import Consommation from '../../../components/others/consommation/consommation';
import ContractGestionAlerteFuite from '../../../components/others/contract-gestion-alerte-fuite/contract-gestion-alerte-fuite';

/**
 * Props du panel des tabs
 */
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

/**
 * Fonction qui crée les tabs
 * @param props propriétés
 * @constructor
 */
function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

/**
 * Permet de définir sur quel tabs on est.
 * @param index tab id
 */
function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

/**
 * Composant de la page d'un contrat
 */
export default function ContractPage(props: { tab?: number }) {
    //Navigate
    const navigate = useNavigate();
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);
    //Use confirm dialog
    const confirm = useConfirm();
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //Position des tabs
    const tabDetails = 0;
    const tabConsommation = 1;
    const tabBills = 2;
    const tabRequests = 3;
    const tabGestionAlerteFuite = 4;

    // State util pour l'affichage du contrat
    const [valueTab, setValueTab] = React.useState(props.tab ? props.tab : 0);
    const { id } = useParams();
    const [contract, setContract] = useState<ContractDto>();
    const [lastBill, setLastBill] = useState<BillDto>();

    /**
     * Permet de gérer sur quelle vue de tab est affichée.
     */
    const handleChangeValueTab = (event: React.SyntheticEvent, newValue: number) => {
        setValueTab(newValue);
    };

    /**
     * Permet de détacher un contrat.
     */
    const handleClickDettacheContract = () => {
        if (id !== undefined) {
            confirm({
                title: 'Êtes-vous certain de vouloir effectuer cette action ?',
                content:
                    "Le contrat ne sera pas résilié, mais il n'apparaîtra plus sur votre compte. Il sera ainsi possible de le rattacher à un autre compte.",
                cancellationText: 'Annuler',
            })
                .then(() => {
                    detachedContract(id)
                        .then(message => {
                            showMessage.successAlert(message);
                            navigate('/mes-contrats');
                        })
                        .catch(error => {
                            console.error('Erreur lors de la récupération du contrat');
                            showAxiosMessageError(error, showMessage);
                        });
                })
                .catch(() => console.warn('Suppression annulée.'));
        }
    };

    /**
     * Permet de détacher un contrat.
     */
    const handleStopDirectDebit = () => {
        if (id !== undefined) {
            confirm({
                title: 'Êtes-vous certain de vouloir effectuer cette action ?',
                description: `Il n'y aura plus de prélévement automatique sur le contrat sélectionné.`,
                cancellationText: 'Annuler',
            })
                .then(() => {
                    stopDirectDebit(id)
                        .then(message => {
                            showMessage.successAlert(message);
                            navigate('/contrat/' + id);
                        })
                        .catch(error => {
                            console.error("Erreur lors de l'arrêt du prélévement automatique");
                            showAxiosMessageError(error, showMessage);
                        });
                })
                .catch(() => console.warn('Arrêt du prélévement automatique annulé.'));
        }
    };

    const handleClickBills = () => {
        setValueTab(1);
    };

    /**
     * Handler qui permet le changement d'état du contrat pour mettre à jour les textes eco geste
     */
    const handleChangeEcoGesture = () => {
        //On ne fait rien si vide
        if (contract !== undefined) {
            setContract({
                ...contract,
                factureElectronique: !contract?.factureElectronique,
            });
        }
    };

    /**
     * Gestion du téléchargement d'une facture
     * @param contractId Id du contrat
     * @param billRef Référence de la facture
     */
    const handleClickDownload = (contractId: string, billRef: string) => {
        getBillBase64(contractId, billRef)
            .then(result => {
                // Conversion du contenu base64 en pdf et ouverture de celui-ci
                fetch(`data:application/pdf;base64,` + result)
                    .then(async pdf => {
                        const file = await pdf.blob();
                        const fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    })
                    .catch(error => {
                        console.error('Erreur lors de la conversion de la facture.');
                        showAxiosMessageError(error, showMessage);
                    });
            })
            .catch((error: Error | AxiosError) => {
                if (axios.isAxiosError(error)) {
                    // Gestion de l'erreur 404 sur la récup du fichier
                    if (error?.response?.status === httpCodeNotFound) {
                        showMessage.errorAlert('Aucun fichier disponible au téléchargement.');
                    } else {
                        showAxiosMessageError(error, showMessage);
                    }
                } else {
                    showAxiosMessageError(error, showMessage);
                }
            });
    };

    /**
     * Récupération du contrat à afficher
     */
    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;

        getContract(id)
            .then(fetchedContract => {
                // Formattage du contrat pour enlever l'url de l'id
                if (fetchedContract['@id'] && typeof fetchedContract['@id'] === 'string') {
                    fetchedContract['@id'] = fetchedContract['@id'].replace('/rattachement_contrats/', '');
                }
                setContract(fetchedContract);
            })
            .catch((error: Error | AxiosError) => {
                if (axios.isAxiosError(error)) {
                    if (error?.response?.status === httpCodeNotFound) {
                        navigate('/erreur-404');
                    } else {
                        showAxiosMessageError(error, showMessage);
                    }
                } else {
                    showAxiosMessageError(error, showMessage);
                }
            });

        getLastBill(id)
            .then(result => {
                if (result?.bills?.length === 1) {
                    setLastBill(result.bills[0]);
                }
            })
            .catch(error => {
                console.error('Erreur lors de la récupération du contrat');
                showAxiosMessageError(error, showMessage);
            });
    }, [showMessage, id, navigate]);

    return (
        //Affichage de tabs
        //Gestion des contenus des différents tabs
        <Box sx={{ padding: 0 }}>
            {contract ? (
                <Typography variant={'h4'}>
                    <Tooltip title={'Retourner à la liste des contrats'}>
                        <IconButton component={Link} to={'/mes-contrats'}>
                            <ArrowBack></ArrowBack>
                        </IconButton>
                    </Tooltip>
                    &nbsp;Contrat&nbsp;{contract?.informations?.abonne?.pdiReference}
                </Typography>
            ) : (
                <Typography variant={'h4'}>
                    <Skeleton variant={'text'}></Skeleton>
                </Typography>
            )}

            <EcoGesture contract={contract} handleChangeEcoGesture={handleChangeEcoGesture}></EcoGesture>
            <Tabs
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                value={valueTab}
                onChange={handleChangeValueTab}
                sx={{ marginBottom: '10px' }}
            >
                <Tab icon={<Info />} label="Détails et services" {...a11yProps(tabDetails)} />
                <Tab icon={<ReceiptLong />} label="Factures" {...a11yProps(tabBills)} />
                <Tab icon={<Equalizer />} label="Consommation" {...a11yProps(tabConsommation)} />
                <Tab icon={<Forum />} label="Demandes" {...a11yProps(tabRequests)} />
                {contract?.telereleve && (
                    <Tab icon={<WaterDamage />} label="Gestion alerte fuite" {...a11yProps(tabGestionAlerteFuite)} />
                )}
            </Tabs>
            {/*Tab Détails du contrat (par défaut)*/}
            <TabPanel value={valueTab} index={0}>
                {contract ? (
                    <ContractDetailsUi
                        contract={contract}
                        lastBill={lastBill}
                        handleClickBills={handleClickBills}
                        handleDettachContract={handleClickDettacheContract}
                        handleStopDirectDebit={handleStopDirectDebit}
                        handleClickDownload={handleClickDownload}
                    />
                ) : (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                )}
            </TabPanel>

            {/*Tab Factures du contrat*/}
            <TabPanel value={valueTab} index={1}>
                {contract ? (
                    <BillsTable contract={contract} />
                ) : (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                )}
            </TabPanel>

            {/*Tab Consommation du contrat*/}
            <TabPanel value={valueTab} index={2}>
                {contract ? (
                    <Consommation contract={contract} />
                ) : (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                )}
            </TabPanel>

            {/*Tab Demandes du contrat*/}
            <TabPanel value={valueTab} index={3}>
                {contract ? (
                    <ContractRequests contract={contract} />
                ) : (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                )}
            </TabPanel>

            {/* Tab Gestion Alerte Fuite */}
            <TabPanel value={valueTab} index={4}>
                {contract ? (
                    <ContractGestionAlerteFuite contract={contract} updateContract={setContract} />
                ) : (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                )}
            </TabPanel>
        </Box>
    );
}
